import { Component, Element, Fragment, h, Prop, State } from '@stencil/core';

@Component({
  tag: 'rentware-image',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RentwareImage {
  @Prop() src: string;
  @Prop() className: string;
  @Prop() alt: string;
  @Prop() vw: number;
  @Prop() vh: number;
  @Prop() placeholderW: number;
  @Prop() placeholderH: number;
  @Prop() autoSize: boolean;
  @Prop() returnGreaterSize: boolean;
  @Prop() forceAspectRatio: boolean;
  @State() autoVw: number;
  @State() autoVh: number;
  @State() imageLoaded: boolean = false;

  @Element() el;
  imageRef: HTMLImageElement;

  componentDidRender() {
    const autoSizingRequired = this.autoSize && !this.autoVw && !this.autoVh;
    if (this.imageRef && autoSizingRequired) {
      const { width, height } = calculateDimensions(this.imageRef, this.returnGreaterSize);
      this.autoVw = width;
      this.autoVh = height;
    }
  }

  handleImageLoaded = () => {
    this.imageLoaded = true;
  };

  missingImage() {
    const pw = this.placeholderW ? `50px` : '';
    const ph = this.placeholderH ? `50px` : '100%';
    return (
      <svg height={ph} width={pw} opacity={0.8} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 6H25V21C25 23.2091 23.2091 25 21 25H5C2.79086 25 1 23.2091 1 21V6Z" fill="#E2E2E6" />
        <path d="M6 1H20V7H6V1Z" fill="#E2E2E6" />
        <path d="M1 6H25V21C25 23.2091 23.2091 25 21 25H5C2.79086 25 1 23.2091 1 21V6Z" fill="white" />
        <circle r="2" transform="matrix(1 0 0 -1 20 20)" fill="#E2E2E6" />
        <path
          d="M1 6H25M1 6V21C1 23.2091 2.79086 25 5 25H21C23.2091 25 25 23.2091 25 21V6M1 6L4.39951 1.75061C4.77906 1.27618 5.35368 1 5.96125 1H20.0388C20.6463 1 21.2209 1.27618 21.6005 1.75061L25 6M8 11H18"
          stroke="#625F74"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );
  }

  render() {
    let src = this.src;
    if (!src) {
      return this.missingImage();
    }

    const vw = this.autoVw || this.vw;
    const vh = this.autoVh || this.vh;

    if (vw || vh || this.autoSize) {
      // if no width and height calculated yet then render single pixel image which will be used for measuring
      // final dimensions.
      if (!vw && !vh) {
        return (
          <img
            id="one-pix-image"
            ref={el => (this.imageRef = el)}
            // 1px png image
            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8+f9vPQAJZAN2rlRQVAAAAABJRU5ErkJggg=="
            data-src={src}
            class={this.className}
            alt={this.alt}
          />
        );
      }

      src = `https://rentware.pcdn.pixdrill.com/?url=${encodeURIComponent(this.src)}`;
      if (vw) {
        src += `&vw=${vw}`;
      }
      if (vh) {
        src += `&vh=${vh}`;
      }
      if (this.forceAspectRatio) {
        src += `&far=1`;
      }
      if (window.devicePixelRatio) {
        src += `&pr=${window.devicePixelRatio}`;
      }
    }

    const showPlaceholderStyle = {
      display: this.imageLoaded ? 'none' : '',
    };

    const showImageStyle = {
      display: this.imageLoaded ? '' : 'none',
    };

    return (
      <Fragment>
        <div id="one-pix-image" style={showPlaceholderStyle} data-src={src} class={this.className} />

        <img style={showImageStyle} src={src} class={this.className} alt={this.alt} onLoad={this.handleImageLoaded} />
      </Fragment>
    );
  }
}

function calculateDimensions(imageEl: HTMLImageElement, returnGreaterSize: boolean): { width: number; height: number } {
  let width = window.innerWidth;
  let height = 0;

  // prevent suspiciously small width
  if (imageEl.clientWidth > 2) {
    width = imageEl.clientWidth;
  } else {
    const computedStyle = window.getComputedStyle(imageEl);
    if (computedStyle.maxWidth && computedStyle.maxWidth.includes('px')) {
      width = parseInt(computedStyle.maxWidth);
    }
  }

  // prevent suspiciously small height
  if (imageEl.clientHeight > 1) {
    if (returnGreaterSize && imageEl.clientWidth > imageEl.clientHeight) {
      height = imageEl.clientWidth;
    } else {
      height = imageEl.clientHeight;
    }
  }

  return { width, height };
}
